import type { Editor } from '@tiptap/core';

/**
 * The link bubble edits both plain URL links (the `link` mark) and
 * [[wikilinks]] (the `wikiLink` mark) — same preview, same open/edit/remove.
 * These helpers let the bubble and the EditableLink extension act on
 * whichever of the two is under the cursor without caring which it is.
 */
export type LinkMarkName = 'link' | 'wikiLink';

const LINK_MARK_NAMES: LinkMarkName[] = ['link', 'wikiLink'];

export function activeLinkMark(editor: Editor): LinkMarkName | null {
   return LINK_MARK_NAMES.find(name => editor.isActive(name)) ?? null;
}

export function isLinkMarkActive(editor: Editor): boolean {
   return activeLinkMark(editor) !== null;
}

// The two marks record "open in a new window" differently: `link` uses
// target='_blank', `wikiLink` a boolean `newWindow` attr.
export function activeLinkOpensNewWindow(editor: Editor): boolean {
   const mark = activeLinkMark(editor);
   if (mark === 'wikiLink') {
      return Boolean(editor.getAttributes('wikiLink').newWindow);
   }
   if (mark === 'link') {
      return editor.getAttributes('link').target === '_blank';
   }
   return false;
}

// EditableLink's newLinksOpenInNewWindow, for the bubble's new-window switch.
// The bubble reads this while rendering, and destroy() nulls extensionManager.
export function newLinksOpenInNewWindow(editor: Editor): boolean {
   if (editor.isDestroyed) {
      return false;
   }
   const link = editor.extensionManager.extensions.find(extension => extension.name === 'link');
   return Boolean(link?.options.newLinksOpenInNewWindow);
}

/**
 * The target to save when re-applying a wikiLink whose address wasn't changed.
 * A bare wikilink (target === null) names its page by its display text, so
 * editing the text would silently retarget it — [[E-Waste]] renamed to
 * "certified recycler" would point at a page named "certified recycler". When
 * the text diverges, pin the target to the original page so it saves as
 * [[E-Waste|certified recycler]] and keeps its destination. An unchanged text,
 * or an already-pinned target, is returned untouched.
 */
export function wikiLinkSaveTarget(
   attrs: { target?: string | null },
   originalText: string,
   newText: string
): string | null {
   if (attrs.target === null && newText !== originalText) {
      return originalText;
   }
   return attrs.target ?? null;
}
